/**
 * PreSet 수동 입력(flat) → OfficerAppointmentForm contactData 변환
 */

import {
  directorProfileToContactData,
  pickDirectorProfileFromFlat,
} from '@/features/startTask/preset_task/sg/components/appoint-director/composables/directorStructuredProfile';
import {
  getB04Options,
  nationalityLabelForOutput,
  resolveCmBaseCodeValue,
} from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';

export interface ParsedManualAddress {
  typeCd: 'B07001' | 'B07002';
  postalCode: string;
  blockNo: string;
  streetName: string;
  buildingName: string;
  addrLevel: string;
  addrUnit: string;
  foreignAddressLine1: string;
  foreignAddressLine2: string;
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** Full name → First / Middle / Last (e-Filing Personal information) */
export function splitContactDisplayName(fullNameRaw: unknown): {
  firstName: string;
  middleName: string;
  surname: string;
} {
  const parts = trim(fullNameRaw).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', middleName: '', surname: '' };
  if (parts.length === 1) return { firstName: parts[0], middleName: '', surname: '' };
  if (parts.length === 2) return { firstName: parts[0], middleName: '', surname: parts[1] };
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(' '),
    surname: parts[parts.length - 1],
  };
}

function emptyAddress(): ParsedManualAddress {
  return {
    typeCd: 'B07001',
    postalCode: '',
    blockNo: '',
    streetName: '',
    buildingName: '',
    addrLevel: '',
    addrUnit: '',
    foreignAddressLine1: '',
    foreignAddressLine2: '',
  };
}

function asForeignAddress(line: string): ParsedManualAddress {
  const parts = line.split(/,\s*/).map((p) => p.trim()).filter(Boolean);
  return {
    typeCd: 'B07002',
    postalCode: '',
    blockNo: '',
    streetName: '',
    buildingName: '',
    addrLevel: '',
    addrUnit: '',
    foreignAddressLine1: parts[0] || line,
    foreignAddressLine2: parts.slice(1).join(', '),
  };
}

/** 싱가포르 스타일 한 줄 주소 → Local(B07001) 또는 Foreign(B07002) 구조 */
export function parseManualAddressText(raw: unknown): ParsedManualAddress {
  const line = trim(raw);
  if (!line) return emptyAddress();

  const postalMatch =
    line.match(/(?:,\s*)?(?:Singapore\s+)?(\d{6})\s*$/i) ||
    line.match(/\b(\d{6})\s*$/);
  const postalCode = postalMatch ? postalMatch[1] : '';

  if (!postalCode) {
    return asForeignAddress(line);
  }

  let remainder = line
    .replace(/(?:,\s*)?(?:Singapore\s+)?\d{6}\s*$/i, '')
    .replace(/,\s*$/, '')
    .trim();

  let addrLevel = '';
  let addrUnit = '';
  const levelUnitMatch = remainder.match(/#(\d+)\s*[-–]\s*(\d+[A-Za-z]?)/);
  if (levelUnitMatch) {
    addrLevel = levelUnitMatch[1];
    addrUnit = levelUnitMatch[2];
    remainder = remainder.replace(levelUnitMatch[0], '').replace(/,\s*$/, '').trim();
  }

  let blockNo = '';
  const blockMatch = remainder.match(/(?:Blk|Block)\.?\s+(\S+)/i);
  if (blockMatch) {
    blockNo = blockMatch[1].replace(/,$/, '');
    remainder = remainder.replace(blockMatch[0], '').replace(/^,\s*/, '').trim();
  } else {
    const houseMatch = remainder.match(/^(\d+[A-Za-z]?)\s+(.+)$/);
    if (houseMatch) {
      blockNo = houseMatch[1];
      remainder = houseMatch[2].trim();
    }
  }

  const parts = remainder.split(/,\s*/).map((p) => p.trim()).filter(Boolean);
  let streetName = '';
  let buildingName = '';
  if (parts.length >= 2) {
    buildingName = parts[parts.length - 1];
    streetName = parts.slice(0, -1).join(', ');
  } else {
    streetName = parts[0] || remainder;
  }

  if (!streetName && !blockNo) {
    return asForeignAddress(line);
  }

  return {
    typeCd: 'B07001',
    postalCode,
    blockNo,
    streetName,
    buildingName,
    addrLevel,
    addrUnit,
    foreignAddressLine1: '',
    foreignAddressLine2: '',
  };
}

/** NRIC / FIN / Passport 패턴 → L_BASECODE B03 */
export function inferSgIdTypeCd(idNoRaw: unknown): string {
  const idNo = trim(idNoRaw).toUpperCase().replace(/\s/g, '');
  if (!idNo) return '';
  if (/^[ST]\d{7}[A-Z]$/.test(idNo)) return 'B03001';
  if (/^[FGM]\d{7}[A-Z]$/.test(idNo)) return 'B03003';
  return 'B03004';
}

/** SG preset 기본 휴대폰 국가 코드 (L_BASECODE B05 Singapore) */
export const SG_DEFAULT_COUNTRY_CD = 'B05SGP';

export function splitMobileNumber(raw: unknown): { countryCd: string; mobileNo: string; areaCode: string } {
  const text = trim(raw);
  if (!text) {
    return { countryCd: '', mobileNo: '', areaCode: '' };
  }

  const intl = text.match(/^\+?(\d{1,3})[\s-]?(\d[\d\s-]*)$/);
  if (intl) {
    const code = intl[1];
    const mobileNo = intl[2].replace(/\s/g, '');
    return {
      countryCd: code === '65' ? SG_DEFAULT_COUNTRY_CD : '',
      mobileNo,
      areaCode: code === '65' ? '65' : code,
    };
  }

  const digits = text.replace(/\D/g, '');
  if (digits.startsWith('65') && digits.length > 8) {
    return {
      countryCd: SG_DEFAULT_COUNTRY_CD,
      mobileNo: digits.slice(2),
      areaCode: '65',
    };
  }

  return {
    countryCd: SG_DEFAULT_COUNTRY_CD,
    mobileNo: digits || text,
    areaCode: '65',
  };
}

export interface ManualOfficerContactFields {
  CONTACT_NAME: string;
  contactName: string;
  FIRST_NAME: string;
  firstName: string;
  MIDDLE_NAME: string;
  middleName: string;
  SURNAME: string;
  surname: string;
  CONTACT_REG_NO: string;
  contactRegNo: string;
  NATIONALITY_CD: string;
  nationalityCd: string;
  nationalityText: string;
  DATE_OF_BIRTH: string;
  dateOfBirth: string;
  CATEGORY_CD: string;
  categoryCd: string;
  ID_TYPE_CD: string;
  idTypeCd: string;
  ADDRESS_TYPE_CD: string;
  addressTypeCd: string;
  POSTAL_CODE: string;
  BLOCK_NO: string;
  STREET_NAME: string;
  BUILDING_NAME: string;
  ADDR_LEVEL: string;
  ADDR_UNIT: string;
  FOREIGN_ADDRESS_LINE_1: string;
  FOREIGN_ADDRESS_LINE_2: string;
  ALTERNATE_ADDRESS_CD: string;
  alternateAddressCd: string;
  ALT_POSTAL_CODE: string;
  ALT_BLOCK_NO: string;
  ALT_STREET_NAME: string;
  ALT_BUILDING_NAME: string;
  ALT_ADDR_LEVEL: string;
  ALT_ADDR_UNIT: string;
  ALT_FOREIGN_ADDRESS_LINE_1: string;
  ALT_FOREIGN_ADDRESS_LINE_2: string;
  CONTACT_EMAIL: string;
  contactEmail: string;
  MOBILE_NO: string;
  mobileNo: string;
  COUNTRY_CD: string;
  countryCd: string;
  AREA_CODE: string;
  areaCode: string;
  contactCd: string;
}

function applyParsedAddressPrimary(target: ManualOfficerContactFields, parsed: ParsedManualAddress): void {
  target.ADDRESS_TYPE_CD = parsed.typeCd;
  target.addressTypeCd = parsed.typeCd;
  if (parsed.typeCd === 'B07001') {
    target.POSTAL_CODE = parsed.postalCode;
    target.BLOCK_NO = parsed.blockNo;
    target.STREET_NAME = parsed.streetName;
    target.BUILDING_NAME = parsed.buildingName;
    target.ADDR_LEVEL = parsed.addrLevel;
    target.ADDR_UNIT = parsed.addrUnit;
    target.FOREIGN_ADDRESS_LINE_1 = '';
    target.FOREIGN_ADDRESS_LINE_2 = '';
  } else {
    target.POSTAL_CODE = '';
    target.BLOCK_NO = '';
    target.STREET_NAME = '';
    target.BUILDING_NAME = '';
    target.ADDR_LEVEL = '';
    target.ADDR_UNIT = '';
    target.FOREIGN_ADDRESS_LINE_1 = parsed.foreignAddressLine1;
    target.FOREIGN_ADDRESS_LINE_2 = parsed.foreignAddressLine2;
  }
}

function applyParsedAddressAlternate(target: ManualOfficerContactFields, parsed: ParsedManualAddress): void {
  target.ALTERNATE_ADDRESS_CD = parsed.typeCd;
  target.alternateAddressCd = parsed.typeCd;
  if (parsed.typeCd === 'B07001') {
    target.ALT_POSTAL_CODE = parsed.postalCode;
    target.ALT_BLOCK_NO = parsed.blockNo;
    target.ALT_STREET_NAME = parsed.streetName;
    target.ALT_BUILDING_NAME = parsed.buildingName;
    target.ALT_ADDR_LEVEL = parsed.addrLevel;
    target.ALT_ADDR_UNIT = parsed.addrUnit;
    target.ALT_FOREIGN_ADDRESS_LINE_1 = '';
    target.ALT_FOREIGN_ADDRESS_LINE_2 = '';
  } else {
    target.ALT_POSTAL_CODE = '';
    target.ALT_BLOCK_NO = '';
    target.ALT_STREET_NAME = '';
    target.ALT_BUILDING_NAME = '';
    target.ALT_ADDR_LEVEL = '';
    target.ALT_ADDR_UNIT = '';
    target.ALT_FOREIGN_ADDRESS_LINE_1 = parsed.foreignAddressLine1;
    target.ALT_FOREIGN_ADDRESS_LINE_2 = parsed.foreignAddressLine2;
  }
}

/** flat director/secretary 필드 → e-Filing contactData 스냅샷 */
export function buildManualOfficerContactFields(
  src: Record<string, unknown> | null | undefined,
): ManualOfficerContactFields {
  const safe = src && typeof src === 'object' ? src : {};
  const profile = pickDirectorProfileFromFlat(safe);
  const hasStructuredAddress =
    !!trim(safe.directorAddressTypeCd) ||
    !!trim(safe.secretaryAddressTypeCd) ||
    !!trim(profile.primary.postalCode) ||
    !!trim(profile.primary.foreignLine1) ||
    !!trim(profile.primary.streetName) ||
    !!trim(profile.primary.blockNo);

  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = safe[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const displayName = pick(
    'director_name',
    'directorName',
    'new_director_name',
    'secretary_name',
    'secretaryName',
    'new_secretary_name',
    'contactName',
    'officerName',
  );
  const idNo = pick(
    'director_identification_no',
    'directorIdentificationNo',
    'new_director_identification_no',
    'secretary_identification_no',
    'secretaryIdentificationNo',
    'registrationIdNo',
  );
  const nationalityRaw = pick(
    'director_nationality',
    'directorNationality',
    'secretary_nationality',
    'secretaryNationality',
    'nationalityCd',
    'nationality',
    'nationalityText',
  );
  const nationalityCd = resolveCmBaseCodeValue(nationalityRaw, getB04Options());
  const nationalityText = nationalityLabelForOutput(nationalityCd) || nationalityRaw;
  const dob = pick(
    'director_date_of_birth',
    'directorDateOfBirth',
    'secretary_date_of_birth',
    'secretaryDateOfBirth',
    'dateOfBirth',
  );
  const email = pick('director_email', 'directorEmail', 'secretary_email', 'secretaryEmail', 'contactEmail', 'email');
  const mobileRaw = pick('director_contact_no', 'directorContactNo', 'secretary_contact_no', 'secretaryContactNo', 'mobileNo', 'MOBILE_NO');
  const contactCd = pick('contactCd', 'officerNameCd', 'officer_name_cd');
  const idTypeCd = pick('directorIdTypeCd', 'secretaryIdTypeCd', 'idTypeCd', 'ID_TYPE_CD') || profile.idTypeCd;
  const mobileCountryCd = pick('directorMobileCountryCd', 'secretaryMobileCountryCd', 'COUNTRY_CD', 'countryCd') || profile.mobileCountryCd;
  const mobileAreaCode = pick('directorMobileAreaCode', 'secretaryMobileAreaCode', 'AREA_CODE', 'areaCode') || profile.mobileAreaCode;
  const nameParts = splitContactDisplayName(displayName);

  const base: ManualOfficerContactFields = {
    CONTACT_NAME: displayName,
    contactName: displayName,
    FIRST_NAME: nameParts.firstName,
    firstName: nameParts.firstName,
    MIDDLE_NAME: nameParts.middleName,
    middleName: nameParts.middleName,
    SURNAME: nameParts.surname,
    surname: nameParts.surname,
    CONTACT_REG_NO: idNo,
    contactRegNo: idNo,
    NATIONALITY_CD: nationalityCd,
    nationalityCd: nationalityCd,
    nationalityText: nationalityText,
    DATE_OF_BIRTH: dob ? dob.substring(0, 10) : '',
    dateOfBirth: dob ? dob.substring(0, 10) : '',
    CATEGORY_CD: 'B09002',
    categoryCd: 'B09002',
    ID_TYPE_CD: idTypeCd,
    idTypeCd: idTypeCd,
    ADDRESS_TYPE_CD: 'B07001',
    addressTypeCd: 'B07001',
    POSTAL_CODE: '',
    BLOCK_NO: '',
    STREET_NAME: '',
    BUILDING_NAME: '',
    ADDR_LEVEL: '',
    ADDR_UNIT: '',
    FOREIGN_ADDRESS_LINE_1: '',
    FOREIGN_ADDRESS_LINE_2: '',
    ALTERNATE_ADDRESS_CD: '',
    alternateAddressCd: '',
    ALT_POSTAL_CODE: '',
    ALT_BLOCK_NO: '',
    ALT_STREET_NAME: '',
    ALT_BUILDING_NAME: '',
    ALT_ADDR_LEVEL: '',
    ALT_ADDR_UNIT: '',
    ALT_FOREIGN_ADDRESS_LINE_1: '',
    ALT_FOREIGN_ADDRESS_LINE_2: '',
    CONTACT_EMAIL: email,
    contactEmail: email,
    MOBILE_NO: mobileRaw,
    mobileNo: mobileRaw,
    COUNTRY_CD: mobileCountryCd,
    countryCd: mobileCountryCd,
    AREA_CODE: mobileAreaCode,
    areaCode: mobileAreaCode,
    contactCd,
  };

  const addrRaw = pick(
    'director_address',
    'directorAddress',
    'secretary_address',
    'secretaryAddress',
  );
  const altAddrRaw = pick(
    'director_alternate_address',
    'directorAlternateAddress',
    'secretary_alternate_address',
    'secretaryAlternateAddress',
  );

  if (addrRaw) {
    applyParsedAddressPrimary(base, parseManualAddressText(addrRaw));
  } else if (hasStructuredAddress) {
    return directorProfileToContactData(
      profile,
      base as unknown as Record<string, unknown>,
    ) as unknown as ManualOfficerContactFields;
  }

  if (altAddrRaw) {
    applyParsedAddressAlternate(base, parseManualAddressText(altAddrRaw));
  }

  return base;
}
